/**
 * Fereastra tabloului: cele patru de-a gata, si perioada aleasa de om.
 *
 * -----------------------------------------------------------------------------
 * DE CE SOCOTEALA E AICI SI NU IN COMPONENTA
 * -----------------------------------------------------------------------------
 *
 * Fiindca ACELEASI reguli le are si serverul, in `Scope::setRange()`, si
 * trebuie sa spuna amandoua acelasi lucru. Ecranul verifica devreme, ca omul sa
 * afle inainte sa se duca pagina; serverul verifica oricum, fiindca adresa se
 * scrie de mana si se trimite mai departe altcuiva.
 *
 * Textele de mai jos sunt scrise ca ale serverului, cuvant cu cuvant. Doua
 * mesaje care spun acelasi lucru altfel il fac pe om sa creada ca sunt doua
 * greseli diferite.
 */

export interface BoardRange {
  from: string
  to: string
}

/** Cat tine cea mai lunga fereastra, cand serverul nu ne-a spus inca. */
export const MAX_DAYS = 90

export function ymd(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

export function today(now: Date = new Date()): string {
  return ymd(now)
}

function isDay(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const d = new Date(value + 'T12:00:00')
  return !Number.isNaN(d.getTime()) && ymd(d) === value
}

/**
 * Cate zile sunt de la o zi la alta, amandoua numarate.
 *
 * Pe amiaza, nu pe miezul noptii: peste schimbarea orei de vara diferenta e de
 * 23 sau 25 de ore, si impartirea ar pierde sau ar castiga o zi. Aceeasi
 * socoteala ca `Scope::between()`.
 */
export function daysBetween(from: string, to: string): number {
  const a = new Date(from + 'T12:00:00').getTime()
  const b = new Date(to + 'T12:00:00').getTime()
  return Math.max(1, Math.round((b - a) / 86400000) + 1)
}

/**
 * Perioada ceruta e buna? '' = da; altfel motivul, scris pentru om.
 *
 * NU taie si nu ghiceste: o perioada prea lunga nu devine una de 90 de zile.
 */
export function checkRange(from: string, to: string, maxDays: number = MAX_DAYS, now: Date = new Date()): string {
  if (!isDay(from) || !isDay(to)) {
    return 'A custom period needs a start date and an end date, both as YYYY-MM-DD.'
  }
  if (to < from) {
    return 'The end of the period cannot be before its start.'
  }
  if (to > today(now)) {
    return 'The period cannot reach past today.'
  }
  const days = daysBetween(from, to)
  if (days > maxDays) {
    return `The longest period the cards can be read over is ${maxDays} days; this one is ${days}.`
  }
  return ''
}

/**
 * Coada de adresa a ferestrei: `days=`, si perioada cand e una.
 *
 * `days` pleaca MEREU, si cand e perioada: ecranele din spatele cardurilor nu
 * stiu toate de la - pana la, iar unul care nu stie trebuie macar sa nimereasca
 * o fereastra de aceeasi lungime, nu implicitul lui de 30 de zile. Aceeasi
 * hotarare ca in `Scope::link()`.
 */
export function windowQuery(days: number, klass: string, range: BoardRange | null): string {
  const p = new URLSearchParams()
  p.set('days', String(range ? daysBetween(range.from, range.to) : days))
  p.set('class', klass)
  if (range) {
    p.set('from', range.from)
    p.set('to', range.to)
  }
  return p.toString()
}

/** Perioada din raspunsul serverului, cand e una asezata. */
export function rangeOf(scope: { from?: string | null; to?: string | null }): BoardRange | null {
  const from = scope.from ?? ''
  const to = scope.to ?? ''
  return from !== '' && to !== '' ? { from, to } : null
}

/** Cum se citeste perioada pe buton: "14 Jun - 7 Aug 2026". */
export function rangeLabel(range: BoardRange): string {
  const a = new Date(range.from + 'T12:00:00')
  const b = new Date(range.to + 'T12:00:00')
  const month = (d: Date) => d.toLocaleString('en', { month: 'short' })
  const same = a.getFullYear() === b.getFullYear()
  const left = `${a.getDate()} ${month(a)}${same ? '' : ' ' + a.getFullYear()}`
  return `${left} - ${b.getDate()} ${month(b)} ${b.getFullYear()}`
}
